import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, ShieldCheck, CheckCircle2, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import BrochureDownload from '@/components/site/BrochureDownload';
import AfterSubmitSteps from '@/components/site/AfterSubmitSteps';
import AnalyticsBeacon from '@/components/AnalyticsBeacon';

// =============================================================================
// /fse — Standalone lead-capture landing page for the Instagram Reel campaign.
//
// COMMERCIAL GOAL: maximize qualified leads from a Reel link sticker.
// The PDF is the lead magnet. The visitor MUST submit the form BEFORE seeing
// the download — the direct PDF URL exists only as a post-submit asset.
//
// LAYOUT — single H1, no duplication, mobile-first lead-gen optimised:
//   • MOBILE  → eyebrow + H1 + short subhead → FORM immediately → bullets +
//                trust strip BELOW the form (under the CTA)
//   • DESKTOP → 2 columns: left hook+bullets+trust, right form card
//
// THE BROCHURE DOWNLOAD MECHANISM IS REUSED. This page ONLY configures it
// (copy, downloadUrl, leadSource, campaign, assetRequested).
// =============================================================================

const FSE_PDF_URL = '/downloads/prime-vision-fse-2-0.pdf';
const FSE_ASSET_ID = 'prime-vision-fse-2-0.pdf';

const COPY = {
  it: {
    metaTitle: 'Guida gratuita FSE 2.0 per studi oculistici · Prime Vision',
    back: 'Torna al sito',
    wa: 'WhatsApp',
    eyebrow: 'Guida gratuita FSE 2.0 per studi oculistici',
    h1_a: 'FSE senza caos:',
    h1_b: 'cosa cambia davvero per il tuo studio.',
    sub_short:
      'Una guida pratica per preparare cartella clinica, documenti e percorso FSE 2.0.',
    sub_long:
      'Una guida pratica per capire come preparare cartella clinica, documenti e percorso FSE 2.0 senza aggiungere altro lavoro alla segreteria.',
    bullets: [
      'Cosa cambia operativamente con il FSE.',
      'Dove il tuo studio rischia di perdere tempo.',
      'Come Prime Vision riduce passaggi manuali e dispersione dati.',
    ],
    bulletsTitle: 'Cosa trovi nella guida',
    trust: 'PDF gratuito · nessun impegno · ricevi subito la guida',
    footer: 'Prime Vision · Software gestionale oculistico · primevision.app',
    pdfLine: 'Guida FSE 2.0 · PDF',
  },
  en: {
    metaTitle: 'Free FSE 2.0 guide for eye-care practices · Prime Vision',
    back: 'Back to site',
    wa: 'WhatsApp',
    eyebrow: 'Free FSE 2.0 guide for eye-care practices',
    h1_a: 'FSE without chaos:',
    h1_b: 'what really changes for your practice.',
    sub_short:
      'A practical guide to prepare medical records, documents and the FSE 2.0 process.',
    sub_long:
      'A practical guide to prepare medical records, documents and the FSE 2.0 process — without adding more work for the front desk.',
    bullets: [
      'What operationally changes with FSE.',
      'Where your practice risks losing time.',
      'How Prime Vision reduces manual steps and data scatter.',
    ],
    bulletsTitle: 'What\'s inside the guide',
    trust: 'Free PDF · no commitment · get the guide instantly',
    footer: 'Prime Vision · Ophthalmology practice management · primevision.app',
    pdfLine: 'FSE 2.0 guide · PDF',
  },
};

export const FsePage = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  // Mutually-exclusive mobile/desktop layouts.
  // Default to mobile to avoid hydration mismatch & to keep the first paint
  // truthful on Reel traffic (which is overwhelmingly mobile).
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    document.title = c.metaTitle;
  }, [c.metaTitle]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    // Modern browsers
    if (mq.addEventListener) {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    // Legacy fallback
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);

  // Reused FORM block — instantiated in both mobile and desktop trees.
  // Single source of truth (same BrochureDownload mechanism, same props).
  // We render it twice but in mutually-exclusive `lg:hidden` / `hidden lg:block`
  // containers, so only one is mounted per viewport breakpoint.
  const renderForm = () => (
    <BrochureDownload
      variant="fse"
      embedded
      hideCopyColumn
      id="fse-brochure"
      testIdPrefix="pv-fse-brochure"
      downloadUrl={FSE_PDF_URL}
      assetId={FSE_ASSET_ID}
      leadSource="fse-brochure"
      fallbackSource="fse-brochure"
      campaign="fse-reel"
      captureUTM
      requirePhone
      showExtraFields
    />
  );

  // Reused BULLETS + TRUST block (mobile + desktop trees).
  const renderBulletsAndTrust = ({ mobile = false } = {}) => (
    <>
      {mobile && (
        <h2 className="font-mono text-[11px] uppercase tracking-[0.22em] text-pv-teal font-bold">
          {c.bulletsTitle}
        </h2>
      )}
      <ul className={`${mobile ? 'mt-3' : 'mt-7 md:mt-8'} space-y-2.5`}>
        {c.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 text-[14px] md:text-[15px] text-slate-700">
            <CheckCircle2 className="w-4 h-4 text-pv-orange shrink-0 mt-1" />
            {b}
          </li>
        ))}
      </ul>
      <div className={`${mobile ? 'mt-5' : 'mt-7'} inline-flex items-center gap-2 text-[12.5px] md:text-[13px] font-semibold text-slate-500`}>
        <ShieldCheck className="w-4 h-4 text-pv-teal" />
        {c.trust}
      </div>
    </>
  );

  return (
    <main
      className="min-h-screen bg-white text-pv-ink relative overflow-x-hidden"
      data-testid="pv-fse-page"
    >
      {/* Minimal header — slim on mobile, comfortable on desktop */}
      <header className="relative z-10 border-b border-slate-200/60 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-12 md:h-18 flex items-center justify-between">
          <Link to="/" data-testid="pv-fse-logo" className="inline-flex items-center gap-2">
            <img src="/assets/prime-vision-logo.png" alt="Prime Vision" className="h-7 md:h-9" />
          </Link>
          <div className="flex items-center gap-3 md:gap-5">
            <a
              href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20info%20sulla%20guida%20FSE%20di%20Prime%20Vision."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pv-fse-wa-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {c.wa}
            </a>
            <Link
              to="/"
              data-testid="pv-fse-back"
              className="inline-flex items-center gap-1.5 text-[12px] md:text-[12.5px] font-semibold text-slate-600 hover:text-pv-teal transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      {/* MOBILE TREE — hook → form → bullets + trust */}
      {!isDesktop && (
      <section className="relative z-10 px-5 pt-4 pb-12" data-testid="pv-fse-mobile">
        {/* Hook — compact */}
        <div>
          <div className="inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-orange" />
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {c.eyebrow}
            </span>
          </div>
          <h1 className="mt-2.5 font-heading font-bold tracking-normal leading-[1.05] text-pv-ink text-[28px] sm:text-[32px]">
            {c.h1_a} <span className="text-pv-teal">{c.h1_b}</span>
          </h1>
          <p className="mt-3 text-slate-600 text-[14.5px] leading-[1.55]">
            {c.sub_short}
          </p>
        </div>

        {/* FORM — immediately under hook */}
        <div className="mt-5">
          {renderForm()}
        </div>

        {/* Bullets + trust — UNDER the form/CTA */}
        <div className="mt-8 border-t border-slate-100 pt-6" data-testid="pv-fse-mobile-aside">
          {renderBulletsAndTrust({ mobile: true })}
        </div>
      </section>
      )}

      {/* DESKTOP TREE — 2 columns, unchanged */}
      {isDesktop && (
      <section className="relative z-10 pt-14 pb-24" data-testid="pv-fse-desktop">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-12 gap-16 items-start">
            {/* LEFT — hook + bullets + trust */}
            <div className="col-span-6 max-w-xl">
              <div className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-orange" />
                <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {c.eyebrow}
                </span>
              </div>
              <h1 className="mt-5 font-heading font-bold tracking-normal leading-[1.05] text-pv-ink text-[52px]">
                {c.h1_a} <span className="text-pv-teal">{c.h1_b}</span>
              </h1>
              <p className="mt-6 text-slate-600 text-[17px] leading-[1.65]">
                {c.sub_long}
              </p>
              {renderBulletsAndTrust()}
            </div>

            {/* RIGHT — form */}
            <div className="col-span-6">
              {renderForm()}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Footer — minimal */}
      <AfterSubmitSteps variant="fse" testId="pv-fse-after-steps" />

      <footer className="relative z-10 border-t border-slate-200/60 bg-white py-5 md:py-6">
        <div className="max-w-5xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-[11px] md:text-[11.5px] text-slate-500 font-medium">
          <span>{c.footer}</span>
          <span className="inline-flex items-center gap-1.5 text-slate-400">
            <FileText className="w-3 h-3" /> {c.pdfLine}
          </span>
        </div>
      </footer>

      <AnalyticsBeacon locale={locale} />
    </main>
  );
};

export default FsePage;
